import React, {
  useEffect, useState, useLayoutEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import Loader from '../../juristec-ui/core/Loader';

import firebase from '../../utils/firebase';
import { getRefUser, getRefCompany, getRefOnRegister } from '../../utils/firestore';
import useSessionStorage from '../../hooks/useSessionStorage';

import authContextCreated from './provider'; // para n estragar os imports no resto do metrics...

import getRequestMeta, { getPublicRequestMeta } from '../../utils/functions/generateMeta';
import { error as errorLabels } from '../../label';
import { ip } from '../../utils/functions/urls';

const supportEmail = () => {
  switch (process.env.REACT_APP_FIREBASE_PROJECT_LABEL) {
    case 'legalone-analytics':
      return 'suporte@legaloneanalytics.com.br';

    case 'benner-metrics':
      return 'suporte@bennermetrics.com.br';

    default:
      return 'suporte@legalmetrics.com.br';
  }
};

export const AuthContext = authContextCreated;

export const AuthProvider = ({ children, hydrate }) => {
  const sessionListener = useRef();
  const [pending, setPending] = useState(true);
  const [msgError, setMsgError] = useState(undefined);
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useSessionStorage('user', null);
  const [claimsUser, setClaimsUser] = useSessionStorage('claims', null);
  const [userCompany, setCompanyUser] = useSessionStorage('company', null);

  function clearSessionStorageAuth() {
    setCompanyUser(null);
    setClaimsUser(null);
    setUser(null);
    setMsgError(undefined);
  }

  function setCompanyPhotoUrl(photoUrl) {
    setCompanyUser({ ...userCompany, photoUrl });
  }

  function changeUserConfigs(configs) {
    setUser({ ...user, configs: { ...user.configs, ...configs } });
  }

  async function getLoginToken(email, password, gToken) {
    try {
      const msg = {
        ...getPublicRequestMeta(gToken, 'GET', '', `${email}:${password}`),
      };
      const res = await fetch(`${ip}/auth/login`, msg);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }

      return { error: false, msg: null, res: json };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  async function checkRecaptchaToken(gToken) {
    try {
      const msg = {
        ...getPublicRequestMeta(gToken, 'GET', ''),
      };
      const res = await fetch(`${ip}/auth/captcha`, msg);

      if (res.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.fetchGeneric, raw: json.error };
      }

      return { error: false, msg: null, res: json };
    } catch (er) {
      // console.log(er);
      return { error: true, msg: errorLabels.fetchGeneric, raw: `Erro do sistema: ${er.toString()}` };
    }
  }

  useLayoutEffect(() => {
    if (currentUser) setPending(false);
    else {
      setPending(true);
    }
  }, [currentUser]);

  useEffect(() => {
    // console.log(session, pending, user)
    if (!pending && user?.session?.length > 0 && currentUser) {
      const checkUserSession = async (doc) => {
        const userDoc = await doc.data();
        if (userDoc.session != user.session) {
          alert(`
            A sessão atual será encerrada, pois uma nova sessão foi iniciada com sua conta.
            Se você não iniciou essa nova sessão, entre em contato com o suporte: ${supportEmail()}.
          `);
          firebase.logout();
        }
      };
      sessionListener.current = firebase.snapShotListener(getRefUser(currentUser.uid), checkUserSession);
    }
    return () => {
      if (sessionListener?.current) sessionListener.current();
    }
  }, [pending, currentUser, sessionListener, user]);

  useEffect(() => {
    if (hydrate) {
      // compatibilidade com modalApi
      setCurrentUser(hydrate.currentUser);
      setClaimsUser(hydrate.claimsUser);
      setCompanyUser(hydrate.userCompany);
      setUser(hydrate.user);
      setPending(false);
      return;
    }

    async function getUserInfo(uid) {
      const refUser = getRefUser(uid);
      await refUser
        .get()
        .catch(() => {
          firebase.logout();
          clearSessionStorageAuth();
        })
        .then((elen) => {
          if (elen === undefined || elen === null) {
            firebase.logout();
            clearSessionStorageAuth();
          } else {
            const userData = elen.data();
            setUser({ ...userData, id: elen.id });
            if ('company' in userData && !userCompany) {
              const refCompany = getRefCompany(userData.company);
              refCompany
                .get()
                .then((company) => {
                  setCompanyUser({ ...company.data(), isAdmin: elen.data()?.isAdmin || false, id: company.id });
                });
            } else {
              setCompanyUser({ isAdmin: elen.data()?.isAdmin || false });
            }
          }
        });
    }

    firebase.authStateObserver(async (authUser) => {
      if (authUser) {
        if (!authUser.emailVerified) {
          firebase.auth.currentUser.sendEmailVerification();
          setMsgError('Email não verificado');
          // alert('Verifique sua caixa de email para confirmar seu cadastro!');
          clearSessionStorageAuth();
          firebase.logout();
          return;
        }

        const { email, uid } = firebase.auth.currentUser;
        const userOk = await getRefUser(uid).get();
        if (!userOk.exists) {
          // console.log('onregister?', email);
          const newUser = await getRefOnRegister(email).get();
          // console.log('nail', email);
          if (newUser.exists) {
            const userInfo = await newUser.data();
            // await firebase.db.doc(`onregister/${email}`).delete(); // a responsabilidade de deletar está com o backend
            // console.log('create for ', userInfo);
            const token = await firebase.auth.currentUser.getIdToken();
            // console.log(token);
            const msg = {
              ...await getRequestMeta(token, 'POST', 'JSON'),
              body: JSON.stringify({
                // userName: userInfo.userName,
                // email: userInfo.email,
                plan: userInfo.plan,
                // companyName: userInfo.companyName,
                // document: userInfo.document,
                // erp: userInfo.erp,
                // prefix: userInfo.prefix,
                // phone: userInfo.phone,
                // publiclink: userInfo.publicLink,
                // push: userInfo.push,
                // monitoring: userInfo.monitoring,
                // newMonitoring: userInfo.newMonitoring,
                // newProcess: userInfo.newProcess,
                // oldProcess: userInfo.oldProcess,
              }),
            };
            const res = await fetch(`${ip}/manage/company`, msg);
            const json = await res.json();
            // await firebase.db.doc(`users/${uid}`).update({ lastLogin: authUser.metadata.lastSignInTime });
            getRefUser(uid).update({ lastLogin: new Date(authUser.metadata.lastSignInTime) });
            console.log(json);
          }
        }

        const userOk2 = await getRefUser(uid).get();
        if (!userOk2.exists) {
          clearSessionStorageAuth();
          firebase.logout();
          return;
        }

        // console.log('validado');
        const date = new Date();
        const time = window.localStorage.getItem('lastActionTime');
        if (time && date.getTime() - time >= 3600000) {
          clearSessionStorageAuth();
          firebase.logout();
        }

        try {
          await getRefOnRegister(email).delete();
        } catch (error) {
          console.log(error);
        }
        // await firebase.db.doc(`onregister/${email}`).get()
        //   .then(async (snapshot) => {
        //     if (snapshot.exists) {
        //       await firebase.db.doc(`onregister/${email}`).delete()
        //     }
        //   });
        // Acessando claims
        // window.pendo.initialize({
        //   visitor: {
        //     id: authUser.uid, // Required if user is logged in
        //     email: authUser.email, // Recommended if using Pendo Feedback, or NPS Email
        //     // full_name:    // Recommended if using Pendo Feedback
        //     // role:         // Optional
        //     // You can add any additional visitor level key-values here,
        //     // as long as it's not one of the above reserved names.
        //   },
        //   account: {
        //     id: authUser.uid, // Highly recommended
        //     email: authUser.email,
        //     // name:         // Optional
        //     // is_paying:    // Recommended if using Pendo Feedback
        //     // monthly_value:// Recommended if using Pendo Feedback
        //     // planLevel:    // Optional
        //     // planPrice:    // Optional
        //     // creationDate: // Optional
        //     // You can add any additional account level key-values here,
        //     // as long as it's not one of the above reserved names.
        //   },
        // });
        if (!claimsUser) {
          authUser.getIdTokenResult().then((result) => {
            const companyId = result?.claims?.companyId || null;
            if (!userCompany && companyId) {
              getRefCompany(companyId)
                .get()
                .catch(() => {
                  firebase.logout();
                })
                .then((companyResp) => {
                  setCompanyUser(companyResp.data());
                })
                .catch(() => {
                  firebase.logout();
                });
            }
            const claims = result?.claims || {};
            setClaimsUser(claims);
          });
        }
        // Acessando firebase
        if (!user) {
          await getUserInfo(authUser.uid);
        }
      } else if (user || claimsUser || userCompany) {
        clearSessionStorageAuth();
      }
      setCurrentUser(authUser);
      setPending(false);
    });
  }, []);

  return pending ? (
    !hydrate && <Loader debug="authProvider" />
  ) : (
    <AuthContext.Provider
      value={{
        currentUser,
        claimsUser,
        userCompany,
        user,
        msgError,
        setCompanyPhotoUrl,
        changeUserConfigs,
        getLoginToken,
        checkRecaptchaToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  hydrate: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    currentUser: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    claimsUser: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    userCompany: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    user: PropTypes.object,
  }),
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]).isRequired,
};

AuthProvider.defaultProps = {
  hydrate: null,
};
